import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactSelect from 'react-select';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Chip,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter
} from "@material-tailwind/react";
import {
    PlusIcon,
    FolderIcon,
    PencilSquareIcon,
    ClockIcon,
    PlayIcon,
    ExclamationTriangleIcon
} from "@heroicons/react/24/outline";
import TaskWorkspaceShell from './TaskWorkspaceShell';
import TaskPageHeader from './TaskPageHeader';
import TaskFilterBar from './TaskFilterBar';

const WorkflowList = () => {
    const navigate = useNavigate();
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');
    const [userId] = useState(localStorage.getItem('userId') || '');

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showProjectDialog, setShowProjectDialog] = useState(false);
    const [projectDialogMode, setProjectDialogMode] = useState('create');
    const [editingProject, setEditingProject] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [formError, setFormError] = useState('');
    const [projectLeadOptions, setProjectLeadOptions] = useState([]);
    const [leadOptionsLoading, setLeadOptionsLoading] = useState(false);
    const [projectForm, setProjectForm] = useState({
        name: '',
        description: '',
        color: '#FF5733',
        project_lead_id: ''
    });

    const getInitials = (value) => {
        const text = (value || 'User').trim();
        const parts = text.split(/\s+/).filter(Boolean);
        if (parts.length === 0) return 'U';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    };

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchProjectLeadOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/projects`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setProjects(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProjectLeadOptions = async () => {
        setLeadOptionsLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/projects/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const options = (response.data.data || []).map((user) => ({
                    value: String(user.value || user.id),
                    label: user.label || `${user.name || user.username}`,
                    id: user.id,
                    name: user.name,
                    username: user.username
                }));

                setProjectLeadOptions(options);
            }
        } catch (error) {
            console.error('Error fetching project lead options:', error);
        } finally {
            setLeadOptionsLoading(false);
        }
    };

    const openCreateDialog = () => {
        setProjectDialogMode('create');
        setEditingProject(null);
        setFormError('');
        setProjectForm({
            name: '',
            description: '',
            color: '#FF5733',
            project_lead_id: ''
        });
        setShowProjectDialog(true);
    };

    const openEditDialog = (project) => {
        setProjectDialogMode('edit');
        setEditingProject(project);
        setFormError('');
        setProjectForm({
            name: project.name || '',
            description: project.description || '',
            color: project.color || '#FF5733',
            project_lead_id: project.project_lead_id ? String(project.project_lead_id) : ''
        });
        setShowProjectDialog(true);
    };

    const closeProjectDialog = () => {
        setShowProjectDialog(false);
        setEditingProject(null);
        setFormError('');
        setProjectForm({
            name: '',
            description: '',
            color: '#FF5733',
            project_lead_id: ''
        });
    };

    const handleSubmitProject = async () => {
        if (!projectForm.name.trim()) {
            setFormError('Workflow name is required');
            return;
        }

        setFormError('');
        try {
            const payload = {
                ...projectForm,
                owner_id: userId,
                created_by: userId,
                project_lead_id: projectForm.project_lead_id ? Number.parseInt(projectForm.project_lead_id, 10) : null
            };

            const request = projectDialogMode === 'edit' && editingProject
                ? axios.put(
                    `${rootUrl}/api/projects/${editingProject.project_id || editingProject.id}`,
                    {
                        ...payload,
                        updated_by: userId
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                )
                : axios.post(
                    `${rootUrl}/api/projects`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

            const response = await request;

            if (response.data.success) {
                closeProjectDialog();
                fetchProjects();
            } else {
                setFormError(response.data.message || `Failed to ${projectDialogMode === 'edit' ? 'update' : 'create'} workflow`);
            }
        } catch (error) {
            console.error(`Error ${projectDialogMode === 'edit' ? 'updating' : 'creating'} workflow:`, error);
            setFormError(error?.response?.data?.message || `Failed to ${projectDialogMode === 'edit' ? 'update' : 'create'} workflow`);
        }
    };

    const filteredProjects = projects.filter((project) => {
        const term = searchTerm.toLowerCase();
        return (
            (project.name || '').toLowerCase().includes(term)
            || (project.description || '').toLowerCase().includes(term)
        );
    });

    const projectLeadSelectOptions = projectLeadOptions;

    const selectedProjectLeadOption = projectLeadSelectOptions.find(
        (option) => String(option.value) === String(projectForm.project_lead_id || '')
    ) || null;

    const renderLeadAvatar = (project) => {
        const displayName = project.project_lead_name || project.project_lead_username || 'Unassigned';
        const avatarUrl = project.project_lead_profile_picture ? `${rootUrl}${project.project_lead_profile_picture}` : null;

        return (
            <div className="relative h-11 w-11 shrink-0">
                {avatarUrl ? (
                    <img
                        src={avatarUrl}
                        alt={displayName}
                        className="h-11 w-11 rounded-full object-cover border border-orange-200 bg-white"
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.parentElement?.querySelector('[data-avatar-fallback]');
                            if (fallback) {
                                fallback.classList.remove('hidden');
                            }
                        }}
                    />
                ) : null}
                <div
                    data-avatar-fallback
                    className={`h-11 w-11 rounded-full bg-[#FF5733] text-white flex items-center justify-center text-xs font-semibold border border-orange-200 ${avatarUrl ? 'hidden' : ''}`}
                >
                    {getInitials(displayName)}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] via-[#fffdfb] to-[#EDE4DA] p-6 flex justify-center items-center">
                <Typography>Loading workflows...</Typography>
            </div>
        );
    }

    const archivedCount = projects.filter((project) => project.is_archived).length;

    return (
        <TaskWorkspaceShell>
            <TaskPageHeader
                title="Workflows"
                subtitle="Organize recurring workstreams, track progress, and jump into workflow-specific task views."
                meta={archivedCount > 0 ? (
                    <Chip
                        value={`${archivedCount} archived`}
                        size="sm"
                        className="bg-gray-100 text-gray-600 border border-gray-200"
                    />
                ) : null}
                actions={(
                    <Button
                        className="flex items-center gap-2 bg-[#FF5733] px-5 py-3 shadow-lg shadow-[#FF5733]/20 hover:bg-[#E04E2E] hover:shadow-xl hover:shadow-[#FF5733]/25"
                        onClick={openCreateDialog}
                    >
                        <PlusIcon className="h-5 w-5" />
                        New Workflow
                    </Button>
                )}
            />

            <TaskFilterBar>
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)]">
                    <Input
                        icon={<FolderIcon className="h-5 w-5" />}
                        label="Search workflows..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </TaskFilterBar>

            {filteredProjects.length === 0 ? (
                <Card className="shadow-sm border border-white/70">
                    <CardBody className="text-center py-12">
                        <FolderIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                        <Typography variant="h5" color="gray" className="mb-2">
                            {projects.length === 0 ? 'No workflows yet' : 'No workflows match your search'}
                        </Typography>
                        <Typography color="gray" className="mb-4">
                            {projects.length === 0
                                ? 'Create your first workflow to get started.'
                                : 'Try a different search term or clear the filter.'}
                        </Typography>
                        <Button
                            className="bg-[#FF5733] hover:bg-[#E04E2E]"
                            onClick={openCreateDialog}
                        >
                            Create Workflow
                        </Button>
                    </CardBody>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredProjects.map((project) => (
                        <Card
                            key={project.project_id}
                            className="group cursor-pointer overflow-hidden border-t-4 shadow-sm border border-white/70 h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl"
                            style={{ borderTopColor: project.color || '#FF5733' }}
                            onClick={() => navigate(`/workflows/${project.project_id}`)}
                        >
                            <CardBody className="space-y-4 h-full flex flex-col p-5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-2 min-h-[2rem]">
                                            <span
                                                className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/80 border border-white/70 shadow-sm"
                                                style={{ color: project.color || '#FF5733' }}
                                            >
                                                <FolderIcon
                                                    className="h-5 w-5"
                                                    style={{ color: project.color || '#FF5733' }}
                                                />
                                            </span>
                                            <Typography variant="h5" color="blue-gray" className="truncate">
                                                {project.name}
                                            </Typography>
                                        </div>
                                        <div className="min-h-[2.75rem]">
                                            {project.description ? (
                                                <Typography variant="small" color="gray" className="line-clamp-2 min-h-[2.75rem] text-xs leading-5">
                                                    {project.description}
                                                </Typography>
                                            ) : (
                                                <Typography variant="small" color="gray" className="invisible min-h-[2.75rem] text-xs leading-5">
                                                    No description
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        {project.is_archived && (
                                            <Chip
                                                value="Archived"
                                                size="sm"
                                                className="bg-gray-200 text-gray-700 w-fit"
                                            />
                                        )}
                                        <Button
                                            size="sm"
                                            variant="text"
                                            className="flex items-center gap-2 text-[#FF5733] hover:bg-[#fff4ef]"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                openEditDialog(project);
                                            }}
                                        >
                                            <PencilSquareIcon className="h-4 w-4" />
                                            Edit
                                        </Button>
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-orange-100 bg-gradient-to-b from-orange-50/80 to-white/80 p-4 space-y-3 mt-auto">
                                    <div className="flex items-center gap-3">
                                        {renderLeadAvatar(project)}
                                        <div className="min-w-0 flex-1">
                                            <Typography variant="small" color="gray" className="text-xs font-medium truncate">
                                                <span className="text-gray-500">Lead:</span>{' '}
                                                <span className="text-blue-gray-900">{project.project_lead_name || project.project_lead_username || 'Unassigned'}</span>
                                            </Typography>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2">
                                        <div className="rounded-xl bg-white/95 border border-orange-100 px-2 py-2 flex flex-col items-center justify-center text-center shadow-[0_1px_0_rgba(255,87,51,0.04)]">
                                            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-500">
                                                <ClockIcon className="h-3.5 w-3.5" />
                                            </span>
                                            <Typography variant="small" color="gray" className="sr-only">
                                                Pending
                                            </Typography>
                                            <Typography variant="h6" color="blue-gray" className="leading-none mt-1">
                                                {project.pending_tasks || 0}
                                            </Typography>
                                        </div>
                                        <div className="rounded-xl bg-white/95 border border-orange-100 px-2 py-2 flex flex-col items-center justify-center text-center shadow-[0_1px_0_rgba(255,87,51,0.04)]">
                                            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#eef7ff] text-[#2563eb]">
                                                <PlayIcon className="h-3.5 w-3.5" />
                                            </span>
                                            <Typography variant="small" color="gray" className="sr-only">
                                                Active
                                            </Typography>
                                            <Typography variant="h6" color="blue-gray" className="leading-none mt-1">
                                                {project.active_tasks || 0}
                                            </Typography>
                                        </div>
                                        <div className="rounded-xl bg-white/95 border border-orange-100 px-2 py-2 flex flex-col items-center justify-center text-center shadow-[0_1px_0_rgba(255,87,51,0.04)]">
                                            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#fff4ef] text-[#FF5733]">
                                                <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                                            </span>
                                            <Typography variant="small" color="gray" className="sr-only">
                                                Overdue
                                            </Typography>
                                            <Typography variant="h6" color="blue-gray" className="leading-none mt-1">
                                                {project.overdue_tasks || 0}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog
                open={showProjectDialog}
                handler={closeProjectDialog}
                size="md"
            >
                <DialogHeader>{projectDialogMode === 'edit' ? 'Edit Workflow' : 'Create New Workflow'}</DialogHeader>
                <DialogBody className="space-y-4">
                    {formError && (
                        <Card className="border border-red-200 bg-red-50 shadow-none">
                            <CardBody className="py-3">
                                <Typography variant="small" color="red">
                                    {formError}
                                </Typography>
                            </CardBody>
                        </Card>
                    )}
                    <Input
                        label="Workflow Name"
                        value={projectForm.name}
                        onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                        className="!border-[#FF5733] focus:!border-[#FF5733]"
                    />
                    <div className="space-y-2">
                        <Typography variant="small" color="gray" className="font-semibold">
                            Staff Lead
                        </Typography>
                        <ReactSelect
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isClearable
                            isLoading={leadOptionsLoading}
                            placeholder="Assign a staff lead..."
                            value={selectedProjectLeadOption}
                            options={projectLeadSelectOptions}
                            onChange={(option) => {
                                setProjectForm({
                                    ...projectForm,
                                    project_lead_id: option?.value || ''
                                });
                            }}
                        />
                    </div>
                    <Input
                        label="Description (optional)"
                        value={projectForm.description}
                        onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                    />
                    <div>
                        <Typography variant="small" color="gray" className="mb-2">
                            Workflow Color
                        </Typography>
                        <div className="flex gap-2 flex-wrap">
                            {['#FF5733', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#e74c3c', '#1abc9c'].map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    className={`w-10 h-10 rounded-full border-2 ${projectForm.color === color ? 'border-gray-800' : 'border-gray-300'
                                        }`}
                                    style={{ backgroundColor: color }}
                                    onClick={() => setProjectForm({ ...projectForm, color })}
                                />
                            ))}
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter className="space-x-2">
                    <Button
                        variant="outlined"
                        onClick={closeProjectDialog}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitProject}
                        className="bg-[#FF5733] hover:bg-[#E04E2E]"
                        disabled={leadOptionsLoading}
                    >
                        {projectDialogMode === 'edit' ? 'Save Changes' : 'Create Workflow'}
                    </Button>
                </DialogFooter>
            </Dialog>
        </TaskWorkspaceShell>
    );
};

export default WorkflowList;
